import { ICommandBarItemProps, MessageBar, MessageBarType } from '@fluentui/react';
import { useMsal } from '@azure/msal-react';
import { RouterParams } from 'App';
import { RootState } from 'app/store';
import CustomPanel from 'components/controls/CustomPanel';
import { EViewType } from 'global/Application';
import { BuildVisibilityScope } from 'global/Visibility/GlobalVisibility';
import { IComponentContextMenu } from 'model/Application';
import {
    EBenefitStatus as FAM_EBenefitStatus,
    EProduct as FAM_EProduct,
    IBenefit as FAM_IBenefit,
    IEntityReferenceValue as FAM_IEntityReferenceValue,
    IPlan as FAM_IPlan
} from 'model/FlexibleAttributeModel';
import { useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import BlueRelationship from '.';
import { Constants } from '../../global/Application/Constants';
import BenefitSummary from './Benefit/BenefitSummary';
import BenefitProperties from './BenefitProperties';
import BulkCostShareWizard, { TCostshareWizardVariant } from './BulkCostShareWizard';
import PlanPanel from './Controls/PlanPanel';
import FlexibleFieldView, { EScopeType } from './FlexibleFieldView';
import NonStandardApplyTemplate from './NonStandardApplyTemplate';
import AssociationTemplateApplyPanel from './AssociationTemplateApplyPanel';
import { MsalTokenSource } from 'global/DataSources/MsalTokenSource';
import { AppDataSource } from 'global/DataSources/AppDataSource';
import { loadAssociationTemplateSnapshotAction, syncBenefitFromTemplateAction } from 'global/Application/dataActions';

/*
* Display Index:
* Group configuration
* - BlueRelationship
* - Summary + fields
* - Plans
* - Benefits
* - Group Structure
* - Fees
* Plan
* - BlueRelationship
* Benefit <-
* - BlueRelationship
*/

interface IProps extends TComponentConnectedProps, IComponentContextMenu {
    benefit?: FAM_IBenefit;

    plans?: FAM_IPlan[];
}



// Default Association Template - Same as AssociationTemplates.tsx
const DEFAULT_TEMPLATE_ACCOUNT: FAM_IEntityReferenceValue = {
    id: '09fb458d-49d8-eb11-bacb-000d3a533755',
    name: 'Association Template',
    type: 'entity',
    entityType: 'account'
};

const DEFAULT_TEMPLATE_SNAPSHOT: FAM_IEntityReferenceValue = {
    id: '4ba75527-05c0-f011-bbd3-6045bda8e172',
    name: 'Major Group Associations [Master]',
    type: 'entity',
    entityType: 'bcbs_groupconfigurationsnapshot'
};

const Benefit = (props: IProps) => {
    const isViewOnly = props.state.status.viewType !== EViewType.Modify;
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const routeParams = useParams<RouterParams>();

    const [propertiesIsOpen, setPropertiesIsOpen] = useState(false);
    const [costshareVariant, setCostshareVariant] = useState<TCostshareWizardVariant | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [syncVersion, setSyncVersion] = useState(0);

    // Get association template from Redux
    const associationTemplateSnapshot = useSelector((state: RootState) => state.data.associationTemplateSnapshot);

    const [applyTemplateIsMounted, setApplyTemplateIsMounted] = useState(false);
    const [applyTemplateIsOpen, _setApplyTemplateIsOpen] = useState(false);
    const setApplyTemplateIsOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
        if (!applyTemplateIsMounted)
            setApplyTemplateIsMounted(true);
        _setApplyTemplateIsOpen(value);
    }

    const [applyAssocTemplateMounted, setApplyAssocTemplateMounted] = useState(false);
    const [applyAssocTemplateOpen, _setApplyAssocTemplateOpen] = useState(false);
    const setApplyAssocTemplateOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
        if (value) {
            setApplyAssocTemplateMounted(true);
        }
        _setApplyAssocTemplateOpen(value);
    }
    const currentBenefit = props?.state?.data.json?.benefits?.find(b => b.id === routeParams.benefitId)

    const medicalCategory = props.state.data.config.cache?.map_bcbs_config_category_named.Medical;

    const benefit = props.benefit
        ? props.benefit
        : props.state?.data.json?.benefits?.find(q => q.id === routeParams.benefitId);

    const isFromAssociationTemplate = benefit?.bcbs_fromassociationtemplate === true;
    const disabled = isViewOnly || isFromAssociationTemplate;


    useEffect(() => {
        document.title = `Benefit | ${benefit?.name ?? '-'} (${benefit?.bcbs_name ?? '-'})`
    }, [benefit]);

    // Auto-load association template snapshot if this benefit came from a template and snapshot isn't in Redux yet
    useEffect(() => {
        const isFromTemplate = benefit?.bcbs_fromassociationtemplate === true;
        if (!isFromTemplate || associationTemplateSnapshot) return;

        const snapshotId = benefit?.bcbs_associationtemplatesnapshotid ?? DEFAULT_TEMPLATE_SNAPSHOT.id;
        dispatch(loadAssociationTemplateSnapshotAction(dataSource, snapshotId) as any);
    }, [benefit]);

    // Auto-dismiss success and error messages after 5 seconds
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    useEffect(() => {
        if (errorMessage) {
            const timer = setTimeout(() => {
                setErrorMessage(null);
            }, 8000);
            return () => clearTimeout(timer);
        }
    }, [errorMessage]);

    const handleSyncFromTemplate = async () => {
        if (!benefit) return;

        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            const error = await dispatch(syncBenefitFromTemplateAction(dataSource, benefit) as any);
            if (error) {
                setErrorMessage(error);
            } else {
                setSyncVersion(v => v + 1);
                setSuccessMessage('Benefit successfully synced from association template!');
            }
        } catch (err: any) {
            console.error('Error syncing benefit:', err);
            setErrorMessage(err?.message || 'Failed to sync benefit from template.');
        }
    };

    const contextMenu = useMemo(() => {
        const items: ICommandBarItemProps[] = [];

        // Properties Menu Option — shown if editable AND not a template benefit (template benefits are readonly)
        if (!isViewOnly && !isFromAssociationTemplate) {
            items.push({
                key: 'benefitProperties',
                text: 'Properties',
                iconProps: { iconName: 'Settings' },
                onClick: () => setPropertiesIsOpen(true)
            });
        }

        if (!isViewOnly) {
            // Sync from Template - only for benefits already from association template
            if (isFromAssociationTemplate) {
                items.push({
                    key: 'syncFromTemplate',
                    text: 'Sync from Association Template',
                    iconProps: { iconName: 'Sync' },
                    onClick: () => {
                        void handleSyncFromTemplate();
                    }
                });
            }

            // Apply Template — groups Association Template and Non-Standard Template
            // Only shown for manually added benefits (not already from association template)
            if (!isFromAssociationTemplate) {
                items.push({
                    key: 'applyTemplate',
                    text: 'Apply Template',
                    iconProps: { iconName: 'CloudImportExport' },
                    subMenuProps: {
                        items: [
                            {
                                key: 'applyAssociationTemplate',
                                text: 'Apply Association Template',
                                iconProps: { iconName: 'CloudImportExport' },
                                onClick: () => {
                                    setApplyAssocTemplateOpen(true);
                                },
                            },
                            {
                                key: 'groupConfiguration.nonStandardTemplate.add',
                                text: 'Apply Non-Standard Template',
                                iconProps: { iconName: 'Generate' },
                                onClick: () => {
                                    setApplyTemplateIsOpen(true);
                                },
                            }
                        ]
                    }
                });
            }
        }

        // Bulk Costshare Menu Option — only for non-template, editable benefits
        if (!isViewOnly && !isFromAssociationTemplate)
            if (currentBenefit?.bcbs_config_categoryid === medicalCategory.bcbs_config_categoryid)
                items.push({
                    key: 'bulkCostshare',
                    text: 'Costshare',
                    iconProps: { iconName: 'Money' },
                    subMenuProps: {
                        items: [
                            {
                                key: 'costshareDefault',
                                text: 'Setup Defaults',
                                iconProps: { iconName: 'FavoriteStar' },
                                onClick: () => { setCostshareVariant('defaults') },
                            },
                            {
                                key: 'costshareSearchAndReplace',
                                text: 'Search and Replace',
                                iconProps: { iconName: 'Search' },
                                onClick: () => { setCostshareVariant('searchAndReplace') },
                            }
                        ]
                    }
                });

        return { items: items, key: 'plan' };
    }, [benefit, props.state.status, isFromAssociationTemplate])

    const onRenderPlans = () => {
        if (!benefit)
            return <></>;

        return <PlanPanel plansBenefit={benefit} />;
    }

    const onRenderSummary = () => {
        if (!benefit)
            return <></>;

        return <CustomPanel title={"Summary"}
            canCollapse={true}
            defaultCollapsed={true}
            storageId={"benefit.summary"}
        >
            <BenefitSummary benefit={benefit} />
        </CustomPanel>;
    }

    const onRenderAttributes = () => {
        if (!benefit)
            return <></>;

        return <CustomPanel title={"Attributes"}
            canCollapse={true}
            defaultCollapsed={false}
            storageId={"benefit.attributes"}
        >
            <FlexibleFieldView
                    key={syncVersion}
                    fields={benefit.fields}
                    category={benefit}
                    tiers={benefit.tiers}
                    scope={benefit}
                    disabled={disabled}
                    editableFieldIds={isFromAssociationTemplate ? [
                        'bcbs_initialbenefitperiodrunsfrom',
                        'bcbs_initialbenefitperiodrunsthrough',
                        'bcbs_initialbenefitperiodstarts',
                        'bcbs_initialbenefitperiodends',
                    ] : undefined}
                    visibility={BuildVisibilityScope(
                    null,
                    props.state.data.json,
                    benefit,
                    null)
                }
                cancellation={benefit.status?.value === FAM_EBenefitStatus.Cancelled}
                product={benefit?.product?.value === FAM_EProduct.Yes}
                scopeType={EScopeType.benefit}
            />
        </CustomPanel>;
    }

    const onRenderPanels = () => {
        return <>
            <BulkCostShareWizard
                isOpen={costshareVariant !== null}
                onDismiss={() => { setCostshareVariant(null) }}
                onCancel={() => { setCostshareVariant(null) }}
                variant={costshareVariant ?? 'defaults'}
                currentBenefit={currentBenefit}
            />

            {benefit &&
                <BenefitProperties
                    isOpen={propertiesIsOpen}
                    onDismiss={() => setPropertiesIsOpen(false)}
                    data={{ benefit: benefit }}
                />
            }
        </>;
    }

    const onRenderApplyTemplate = () => {
        if (!applyTemplateIsMounted)
            return <></>

        return <>
            <NonStandardApplyTemplate
                isOpen={applyTemplateIsOpen}
                onDismiss={() => { setApplyTemplateIsOpen(false); setApplyTemplateIsMounted(false) }}
                benefit={benefit}
                isNewNSAndChooseTemplate={false}
            />
        </>;
    }

    const onRenderApplyAssociationTemplate = () => {
        if (!applyAssocTemplateMounted)
            return <></>;

        return (
            <AssociationTemplateApplyPanel
                isOpen={applyAssocTemplateOpen}
                benefit={benefit}
                onDismiss={() => {
                    _setApplyAssocTemplateOpen(false);
                    setApplyAssocTemplateMounted(false);
                }}
                onSuccess={(msg) => setSuccessMessage(msg)}
                onError={(msg) => setErrorMessage(msg)}
            />
        );
    }

    return <BlueRelationship contextMenu={contextMenu}>
        {errorMessage && (
            <MessageBar
                messageBarType={MessageBarType.error}
                onDismiss={() => setErrorMessage(null)}
                dismissButtonAriaLabel="Close"
                styles={{ root: { marginBottom: 10 } }}
            >
                {errorMessage}
            </MessageBar>
        )}
        {successMessage && (
            <MessageBar
                messageBarType={MessageBarType.success}
                onDismiss={() => setSuccessMessage(null)}
                dismissButtonAriaLabel="Close"
                styles={{ root: { marginBottom: 10 } }}
            >
                {successMessage}
            </MessageBar>
        )}
        {benefit?.bcbs_fromassociationtemplate === true && (
            <MessageBar
                messageBarType={MessageBarType.info}
                styles={{ root: { marginBottom: 10 } }}
            >
                {(() => {
                    const templateBenefitName = benefit?.bcbs_associationtemplatebenefitid
                        ? associationTemplateSnapshot?.json?.benefits?.find(
                            (b: FAM_IBenefit) => b.id === benefit.bcbs_associationtemplatebenefitid
                          )?.name
                        : undefined;

                    const snapshotName = associationTemplateSnapshot?.snapshot?.bcbs_name;

                    if (templateBenefitName && snapshotName)
                        return `This benefit was applied from Association Template Benefit: "${templateBenefitName}" of the template "${snapshotName}".`;
                    if (templateBenefitName)
                        return `This benefit was applied from Association Template Benefit: "${templateBenefitName}".`;
                    return 'This benefit was applied from an Association Template.';
                })()}
            </MessageBar>
        )}
        {onRenderApplyTemplate()}
        {onRenderApplyAssociationTemplate()}
        {onRenderPanels()}
        {onRenderSummary()}
        {onRenderPlans()}
        {onRenderAttributes()}
    </BlueRelationship >;

}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(Benefit)

